import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ContactFormPopup } from '../ContactFormPopup';
export function DDServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const {
    t,
    getPath,
    language
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const priceFromLabel: Record<string, string> = {
    en: 'from',
    ru: 'от',
    ua: 'від'
  };
  const fromText = priceFromLabel[language] || 'from';
  const services = [{
    num: '01',
    title: t('dd.services.websites.title'),
    desc: t('dd.services.websites.desc'),
    price: '$590',
    isLink: true,
    href: getPath('/order_website')
  }, {
    num: '02',
    title: t('dd.services.apps.title'),
    desc: t('dd.services.apps.desc'),
    price: '$2 500',
    isLink: false
  }, {
    num: '03',
    title: t('dd.services.ads.title'),
    desc: t('dd.services.ads.desc'),
    price: '$500',
    isLink: false
  }];
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-230">
        <div className="relative z-10 mx-auto max-w-5xl" data-id="element-231">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-12 sm:mb-16 text-center" data-id="element-232">
            <h2 className="text-3xl md:text-4xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-233">
              {t('dd.services.title')} {t('dd.services.titleHighlight')}
            </h2>
          </motion.div>

          {/* Single Column Layout */}
          <div className="relative flex flex-col gap-6 sm:gap-8" data-id="element-234">
            {services.map((service, index) => {
            const CardContent = <div className="group rounded-3xl border p-6 sm:py-7 sm:px-8 relative overflow-hidden transition-all duration-300 hover:shadow-xl cursor-pointer flex flex-col justify-center" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-235">
                  {/* Background Watermark Number (Left side, vertically centered) */}
                  <div className="absolute top-1/2 -translate-y-1/2 left-6 sm:left-8 text-[80px] leading-none font-bold opacity-[0.03] hidden sm:block transition-transform duration-500 group-hover:scale-110 pointer-events-none select-none" style={{
                color: 'var(--text-primary)'
              }} data-id="element-236">
                    {service.num}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-stretch gap-6 sm:gap-8 relative z-10 h-full sm:pl-32" data-id="element-237">
                    {/* Mobile Top: Number + Title */}
                    <div className="flex items-center gap-3 sm:hidden mb-1" data-id="element-238">
                      <span className="text-lg font-bold" style={{
                    color: 'var(--accent)'
                  }} data-id="element-239">
                        {service.num}
                      </span>
                      <h3 className="text-lg font-medium leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-240">
                        {service.title}
                      </h3>
                    </div>

                    {/* Middle: Title (Desktop) & Description */}
                    <div className="flex-1 max-w-3xl flex flex-col justify-center" data-id="element-241">
                      <h3 className="hidden sm:block text-xl sm:text-2xl font-medium leading-tight mb-2" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-242">
                        {service.title}
                      </h3>
                      <p className="text-sm sm:text-base leading-relaxed" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-243">
                        {service.desc}
                      </p>
                    </div>

                    {/* Right Side: Price (Center) & Arrow (Bottom) */}
                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto mt-4 sm:mt-0 flex-shrink-0 min-w-[140px]" data-id="element-244">
                      {/* Price (Vertically centered on desktop) */}
                      <div className="order-1 sm:order-1 flex items-baseline gap-1.5 sm:my-auto" data-id="element-245">
                        <span className="text-xs uppercase tracking-wider font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-246">
                          {fromText}
                        </span>
                        <span className="text-xl sm:text-2xl font-medium" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-247">
                          {service.price}
                        </span>
                      </div>

                      {/* Animated Arrow (Bottom right on desktop) */}
                      <div className="order-2 sm:order-2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--accent)] border flex-shrink-0 sm:mt-auto" style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--bg-primary)'
                  }} data-id="element-248">
                        <ArrowUpRightIcon className="w-5 h-5 transition-all duration-300 group-hover:text-white group-hover:rotate-45" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-249" />
                      </div>
                    </div>
                  </div>
                </div>;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: 0.1 + index * 0.1,
              ease: [0.22, 1, 0.36, 1]
            }} className="relative w-full" data-id="element-250">
                  {service.isLink ? <a href={service.href} className="block h-full" data-id="element-251">
                      {CardContent}
                    </a> : <div onClick={() => setIsPopupOpen(true)} className="h-full" data-id="element-252">
                      {CardContent}
                    </div>}
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-253" />
    </>;
}